// CanalesLimpack — /admin/canales/limpack
// Hub del canal propio: KPIs (Ventas / Operativo / Contable) + tabla de kits activos.
// Fuente: clientes (suscripciones), pedidos, pagos, facturas_emitidas.

import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

export default function CanalesLimpack() {
  const [loading, setLoading] = useState(true)
  const [kits, setKits] = useState([])
  const [suscriptoresPorKit, setSuscriptoresPorKit] = useState({})
  const [stats, setStats] = useState({
    // Ventas
    mrr: 0, ventas_30d: 0, pedidos_30d: 0, ticket_30d: 0, ventas_7d: 0, ventas_7d_prev: 0, pedidos_7d: 0,
    ventas_30d_tienda: 0, ventas_30d_susc: 0, ventas_30d_b2c: 0, ventas_30d_b2b: 0, pedidos_30d_b2c: 0, pedidos_30d_b2b: 0,
    // Operativo
    pendientes_despacho: 0, cobros_fallidos: 0, cfe_pendientes: 0, suscriptores_activos: 0,
    // Contable
    facturado_30d: 0, comision_handy_30d: 0, neto_handy_30d: 0,
    // Suscripción
    suscriptores_pausados: 0, cancelados_30d: 0,
  })

  async function cargar() {
    setLoading(true)
    const now = Date.now()
    const hace7  = new Date(now -  7 * 86400000).toISOString()
    const hace14 = new Date(now - 14 * 86400000).toISOString()
    const hace30 = new Date(now - 30 * 86400000).toISOString()

    const [kitsRes, clientesRes, pedidosB2CRes, pedidosB2BRes, pagosSusc30Res, cobrosFallRes, facturas30Res, comisionesHandyRes] = await Promise.all([
      supabase.from('kits').select('codigo, nombre, tier, precio_susc, precio_unica, activo').eq('activo', true).order('orden'),
      // Solo datos reales (excluye es_prueba en las 5 tablas)
      supabase.from('clientes').select('id, estado, kit_actual, cancelled_at, paused_at').eq('es_prueba', false),
      // B2C: pedidos con pagado_at (personas físicas)
      supabase.from('pedidos').select('id, monto_total, estado, pagado_at, entregado_at').eq('es_prueba', false).is('empresa_id', null).gte('pagado_at', hace30),
      // B2B: pedidos con empresa entregados/en_ruta/pagado (se cobran vía facturas_emitidas.cobro_b2b_estado, no pagado_at)
      supabase.from('pedidos').select('id, monto_total, estado, empresa_id, entregado_at, created_at').eq('es_prueba', false).not('empresa_id', 'is', null).in('estado', ['entregado', 'en_ruta', 'pagado']).gte('created_at', hace30),
      supabase.from('pagos').select('id, monto, pagado_at').eq('estado', 'pagado').eq('es_prueba', false).gte('pagado_at', hace30),
      supabase.from('pagos').select('id', { count: 'exact', head: true }).eq('estado', 'fallido').eq('es_prueba', false).gte('created_at', hace30),
      supabase.from('facturas_emitidas')
        .select('id, monto_total, tipo_cfe, fecha_emision')
        .eq('es_prueba', false)
        .gte('fecha_emision', hace30.slice(0, 10)),
      supabase.from('asientos_contables_lineas')
        .select('debe, asientos_contables!inner(fecha)')
        .eq('cuenta_codigo', '511306')
        .gte('asientos_contables.fecha', hace30.slice(0, 10)),
    ])

    const kitsData = kitsRes.data || []
    const clientes = clientesRes.data || []
    const activos = clientes.filter(c => c.estado === 'activa')
    const pausados = clientes.filter(c => c.paused_at && !c.cancelled_at)
    const cancelados30 = clientes.filter(c => c.cancelled_at && c.cancelled_at >= hace30)

    // MRR = precio_susc * cantidad_activos por kit
    const kitPrecio = Object.fromEntries(kitsData.map(k => [k.codigo, Number(k.precio_susc || 0)]))
    const mrr = activos.reduce((s, c) => s + (kitPrecio[c.kit_actual] || 0), 0)

    // Suscriptores por kit (para tabla)
    const suscPorKit = {}
    for (const c of activos) {
      if (!c.kit_actual) continue
      suscPorKit[c.kit_actual] = (suscPorKit[c.kit_actual] || 0) + 1
    }

    // Pedidos tienda B2C + B2B + pagos de suscripción
    const pedidosB2C = pedidosB2CRes.data || []
    const pedidosB2B = pedidosB2BRes.data || []
    const pedidosTienda = [...pedidosB2C, ...pedidosB2B]
    const pagosSusc30 = pagosSusc30Res.data || []

    // Para windowing 7d/14d: usar pagado_at para B2C y created_at para B2B
    const fechaRefPedido = (p) => p.pagado_at || p.created_at || p.entregado_at
    const p7Tienda  = pedidosTienda.filter(p => fechaRefPedido(p) >= hace7)
    const p14Tienda = pedidosTienda.filter(p => fechaRefPedido(p) >= hace14 && fechaRefPedido(p) < hace7)
    const p7Susc    = pagosSusc30.filter(p => p.pagado_at >= hace7)
    const p14Susc   = pagosSusc30.filter(p => p.pagado_at >= hace14 && p.pagado_at < hace7)

    const brutoB2C      = pedidosB2C.reduce((s, p) => s + Number(p.monto_total || 0), 0)
    const brutoB2B      = pedidosB2B.reduce((s, p) => s + Number(p.monto_total || 0), 0)
    const brutoTienda30 = brutoB2C + brutoB2B
    const brutoSusc30   = pagosSusc30.reduce((s, p) => s + Number(p.monto || 0), 0)
    const bruto30       = brutoTienda30 + brutoSusc30
    const pedidos30Tot  = pedidosTienda.length + pagosSusc30.length

    const bruto7  = p7Tienda.reduce((s, p) => s + Number(p.monto_total || 0), 0) + p7Susc.reduce((s, p) => s + Number(p.monto || 0), 0)
    const bruto14 = p14Tienda.reduce((s, p) => s + Number(p.monto_total || 0), 0) + p14Susc.reduce((s, p) => s + Number(p.monto || 0), 0)
    const pedidos7Tot = p7Tienda.length + p7Susc.length

    const pendDespacho = pedidosTienda.filter(p => !p.entregado_at).length

    // Facturación (excluye notas crédito para "facturado neto")
    const fact30 = facturas30Res.data || []
    const facturado30 = fact30
      .filter(f => f.tipo_cfe !== 'e_nota_credito')
      .reduce((s, f) => s + Number(f.monto_total || 0), 0)
    const notasCredito30 = fact30
      .filter(f => f.tipo_cfe === 'e_nota_credito')
      .reduce((s, f) => s + Number(f.monto_total || 0), 0)
    const facturadoNeto = facturado30 - notasCredito30

    // Comisión Handy real desde asientos contables 30d
    const comisionHandy30 = (comisionesHandyRes.data || []).reduce((s, l) => s + Number(l.debe || 0), 0)

    // CFE pendientes: pedidos pagados sin factura (solo reales)
    const { count: cfePend } = await supabase.from('pedidos')
      .select('id', { count: 'exact', head: true })
      .not('pagado_at', 'is', null)
      .eq('es_prueba', false)
      .gte('pagado_at', hace30)
    // resto = pagados - facturas (aproximación)
    const cfePendientes = Math.max(0, (cfePend || 0) - fact30.filter(f => f.tipo_cfe !== 'e_nota_credito').length)

    setKits(kitsData)
    setSuscriptoresPorKit(suscPorKit)
    setStats({
      mrr,
      ventas_30d: bruto30, pedidos_30d: pedidos30Tot,
      ventas_30d_tienda: brutoTienda30, ventas_30d_susc: brutoSusc30,
      ventas_30d_b2c: brutoB2C, ventas_30d_b2b: brutoB2B,
      pedidos_30d_b2c: pedidosB2C.length, pedidos_30d_b2b: pedidosB2B.length,
      ticket_30d: pedidos30Tot ? Math.round(bruto30 / pedidos30Tot) : 0,
      ventas_7d: bruto7,
      ventas_7d_prev: bruto14,
      pedidos_7d: pedidos7Tot,
      pendientes_despacho: pendDespacho,
      cobros_fallidos: cobrosFallRes.count || 0,
      cfe_pendientes: cfePendientes,
      suscriptores_activos: activos.length,
      facturado_30d: facturadoNeto,
      comision_handy_30d: comisionHandy30,
      neto_handy_30d: facturadoNeto - comisionHandy30,
      suscriptores_pausados: pausados.length,
      cancelados_30d: cancelados30.length,
    })
    setLoading(false)
  }

  useEffect(() => { cargar() }, [])

  const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })
  const pct = stats.ventas_30d ? (stats.neto_handy_30d / stats.ventas_30d * 100).toFixed(1) : '—'

  return (
    <AdminLayout title="Limpack">
      <div className="lp-wrap">
        <header className="lp-head">
          <div>
            <div className="lp-eyebrow">CANALES · LIMPACK</div>
            <h1 className="lp-title">limpack.uy</h1>
            <div className="lp-sub">Tienda propia + suscripciones mensuales. MercadoPago como procesador.</div>
          </div>
          <div className="lp-head-acts">
            <button className="lp-btn-primary" onClick={cargar}>↻ Actualizar</button>
            <span className="lp-pill ok">● Activo</span>
          </div>
        </header>

        {/* Fila 1 · VENTAS */}
        <div className="lp-kpi-group">
          <div className="lp-kpi-title">🛒 VENTAS</div>
          <div className="lp-kpis">
            <KPI lbl="MRR (Recurrente/mes)" num={fmt(stats.mrr)} delta={`${stats.suscriptores_activos} suscriptores`} deltaClass="ok" />
            <KPI lbl="Últimos 7 días" num={fmt(stats.ventas_7d)}
              delta={(() => {
                const d = stats.ventas_7d_prev
                if (d === 0) return `${stats.pedidos_7d} pedidos`
                const p = ((stats.ventas_7d - d) / d * 100).toFixed(0)
                return `${stats.pedidos_7d} pedidos · ${p >= 0 ? '↑' : '↓'} ${Math.abs(p)}% vs 7d prev`
              })()}
              deltaClass={stats.ventas_7d >= stats.ventas_7d_prev ? 'ok' : 'warn'} />
            <KPI lbl="Últimos 30 días" num={fmt(stats.ventas_30d)}
              delta={`${stats.pedidos_30d} pedidos · Susc ${fmt(stats.ventas_30d_susc)} · B2C ${fmt(stats.ventas_30d_b2c || 0)} · B2B ${fmt(stats.ventas_30d_b2b || 0)}`}
              deltaClass="mute" />
            <KPI lbl="Ticket promedio" num={fmt(stats.ticket_30d)} delta="30 días" deltaClass="mute" />
          </div>
        </div>

        {/* Fila 2 · OPERATIVO */}
        <div className="lp-kpi-group">
          <div className="lp-kpi-title">⚡ OPERATIVO</div>
          <div className="lp-kpis">
            <KPI lbl="Pendientes despacho" num={stats.pendientes_despacho}
              delta={stats.pendientes_despacho > 0 ? '→ ir a Entregas' : 'sin backlog'}
              deltaClass={stats.pendientes_despacho > 0 ? 'warn' : 'ok'} />
            <KPI lbl="Cobros fallidos" num={stats.cobros_fallidos}
              delta={stats.cobros_fallidos > 0 ? '⚠ reintentar' : 'todo cobrado'}
              deltaClass={stats.cobros_fallidos > 0 ? 'warn' : 'ok'} />
            <KPI lbl="CFE sin emitir" num={stats.cfe_pendientes}
              delta={stats.cfe_pendientes > 0 ? 'pedido pagado sin CFE' : 'todo facturado'}
              deltaClass={stats.cfe_pendientes > 0 ? 'warn' : 'ok'} />
            <KPI lbl="Pausados / Cancelados 30d" num={`${stats.suscriptores_pausados} / ${stats.cancelados_30d}`}
              delta={stats.cancelados_30d > 5 ? '⚠ churn alto' : 'churn ok'}
              deltaClass={stats.cancelados_30d > 5 ? 'warn' : 'mute'} />
          </div>
        </div>

        {/* Fila 3 · CONTABLE */}
        <div className="lp-kpi-group">
          <div className="lp-kpi-title">📒 CONTABLE (30 días)</div>
          <div className="lp-kpis">
            <KPI lbl="Facturado neto" num={fmt(stats.facturado_30d)} delta="e-Ticket + e-Factura − NC" deltaClass="mute" />
            <KPI lbl="Comisiones MP" num={fmt(stats.comision_handy_30d)} delta="MercadoPago · cuenta 511306" deltaClass="mute" />
            <KPI lbl="Neto Limpack" num={fmt(stats.neto_handy_30d)} delta="facturado − comisión" deltaClass="ok" />
            <KPI lbl="% Neto / Bruto" num={typeof pct === 'string' ? `${pct}%` : '—'} delta="rentabilidad canal" deltaClass="mute" />
          </div>
        </div>

        {/* Kits + suscriptores */}
        <div className="lp-table-wrap">
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #E6E2D8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1A3461' }}>Distribución por kit</div>
            <Link to="/admin/kits" style={{ fontSize: 11.5, color: '#00A8B5' }}>Gestionar kits →</Link>
          </div>
          <table className="lp-table">
            <thead>
              <tr>
                <th>Kit</th>
                <th>Tier</th>
                <th style={{ textAlign: 'right' }}>Precio susc.</th>
                <th style={{ textAlign: 'right' }}>Precio única</th>
                <th style={{ textAlign: 'right' }}>Suscriptores</th>
                <th style={{ textAlign: 'right' }}>MRR aportado</th>
                <th style={{ textAlign: 'right' }}>% MRR</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#888780' }}>Cargando…</td></tr>
              ) : kits.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#888780' }}>No hay kits activos.</td></tr>
              ) : kits.map(k => {
                const cant = suscriptoresPorKit[k.codigo] || 0
                const aporta = cant * Number(k.precio_susc || 0)
                const pctMrr = stats.mrr ? (aporta / stats.mrr * 100).toFixed(1) : 0
                return (
                  <tr key={k.codigo}>
                    <td style={{ fontWeight: 600, color: '#1A3461' }}>{k.nombre}</td>
                    <td><span className="lp-tier-pill">{k.tier}</span></td>
                    <td style={{ textAlign: 'right' }}>{fmt(k.precio_susc)}</td>
                    <td style={{ textAlign: 'right', color: '#888780' }}>{fmt(k.precio_unica)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{cant}</td>
                    <td style={{ textAlign: 'right', color: '#1D9E75', fontWeight: 700 }}>{fmt(aporta)}</td>
                    <td style={{ textAlign: 'right', color: '#888780', fontSize: 11 }}>{pctMrr}%</td>
                  </tr>
                )
              })}
              {!loading && kits.length > 0 && (
                <tr style={{ background: '#F8F6F1', fontWeight: 700 }}>
                  <td colSpan={4} style={{ padding: '10px 12px', color: '#1A3461' }}>TOTAL MRR</td>
                  <td style={{ textAlign: 'right' }}>{stats.suscriptores_activos}</td>
                  <td style={{ textAlign: 'right', color: '#1D9E75' }}>{fmt(stats.mrr)}</td>
                  <td style={{ textAlign: 'right', color: '#888780' }}>100%</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Estilos />
    </AdminLayout>
  )
}

function KPI({ lbl, num, delta, deltaClass = '' }) {
  return (
    <div className="lp-kpi">
      <div className="lp-kpi-lbl">{lbl}</div>
      <div className="lp-kpi-num">{num}</div>
      <div className={`lp-kpi-delta ${deltaClass}`}>{delta}</div>
    </div>
  )
}

function Estilos() {
  return (
    <style>{`
      .lp-wrap { max-width: 1500px; margin: 0 auto; padding: 0 4px 60px; }
      .lp-head { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 14px 18px; margin-bottom: 12px; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
      .lp-eyebrow { font-size: 10.5px; letter-spacing: 0.8px; color: #888780; text-transform: uppercase; font-weight: 600; }
      .lp-title { margin: 4px 0; font-size: 22px; font-weight: 600; color: #1A3461; }
      .lp-sub { font-size: 12.5px; color: #5F5E5A; }
      .lp-head-acts { display: flex; flex-direction: column; gap: 6px; align-items: flex-end; }
      .lp-btn-primary { background: #1A3461; color: #fff; border: 0; padding: 8px 14px; border-radius: 8px; font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit; }
      .lp-btn-ghost { background: #fff; color: #1A3461; border: 1px solid #E6E2D8; padding: 8px 14px; border-radius: 8px; font-size: 12.5px; cursor: pointer; font-family: inherit; text-decoration: none; }
      .lp-pill { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; }
      .lp-pill.ok { background: #E1F5EE; color: #085041; }
      .lp-kpi-group { margin-bottom: 12px; }
      .lp-kpi-title { font-size: 10.5px; letter-spacing: 0.8px; color: #888780; text-transform: uppercase; font-weight: 700; margin-bottom: 6px; padding-left: 2px; }
      .lp-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
      .lp-kpi { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 13px 16px; }
      .lp-kpi-lbl { font-size: 10px; letter-spacing: 0.8px; color: #888780; text-transform: uppercase; font-weight: 600; margin-bottom: 5px; }
      .lp-kpi-num { font-size: 24px; font-weight: 700; color: #1A3461; line-height: 1; }
      .lp-kpi-delta { font-size: 11px; margin-top: 5px; font-weight: 500; }
      .lp-kpi-delta.ok { color: #1D9E75; }
      .lp-kpi-delta.warn { color: #BA7517; }
      .lp-kpi-delta.mute { color: #888780; font-weight: 400; }
      .lp-table-wrap { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; overflow: hidden; margin-top: 8px; }
      .lp-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
      .lp-table thead th { text-align: left; padding: 9px 12px; background: #F8F6F1; font-size: 10.5px; letter-spacing: 0.5px; text-transform: uppercase; color: #888780; font-weight: 600; border-bottom: 1px solid #E6E2D8; }
      .lp-table tbody td { padding: 10px 12px; border-bottom: 1px solid #F1EFE8; vertical-align: middle; }
      .lp-table tbody tr:hover { background: #F8F6F1; }
      .lp-tier-pill { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 10.5px; font-weight: 600; background: #F1F5F9; color: #475569; text-transform: capitalize; }
    `}</style>
  )
}
